import React, { useState } from "react";
import "./bundleslist.css";

// components imports
import PackagesTable from "../../../../../../../Components/BoltTable/BoltTable";
import Tooltip from "../../../../../../../Components/Tooltip/Tooltip";
import Confirm from "../../../../../../../Components/Popups/Confirm";
import UpdateBundle from "../UpdateBundle/UpdateBundle";
import BundleDetails from "../BundleDetails/BundleDetails";
import AddBundle from "../AddBundle/AddBundle";

// apis imports
import {
  fetchAllBundles,
  deleteBundle,
} from "../../../../../../../services/ShipperServices/Packages/packages.services";

// library imports
import { FaRegEdit } from "react-icons/fa";
import { MdAdd, MdOutlineCancel, MdRemoveRedEye } from "react-icons/md";
import noImage from "../../../../../../../assets/images/noImage.png";
import BoltButton from "../../../../../../../Components/UI/Buttons/BoltButton";
import { useTranslation } from "react-i18next";

const BundlesList = ({ snack, setSnack }) => {
  const { t } = useTranslation("products");
  const [deleteLoader, setDeleteLoader] = useState(false);
  const [refresh, setRefresh] = useState(false);
  const [bundleDetailsModal, setBundleDetailsModal] = useState();
  const [deleteBundleModal, setDeleteBundleModal] = useState(false);
  const [updateBundleModal, setUpdateBundleModal] = useState(false);
  const [addBundleModal, setAddBundleModal] = useState(false);

  const [bundles, setBundles] = useState([]);

  const columns = [
    {
      id: 0,
      label: "#",
      render: (row) => <>{row?.id ?? "-"}</>,
      width: "200px",
    },
    {
      id: 1,
      label: "image",
      field_name: "Image",
      render: (row, rowIndex, handleFieldChange) => (
        <>
          <img
            src={row.file ?? noImage}
            onError={(e) => {
              e.target.src = noImage;
            }}
          />
        </>
      ),
      width: "200px",
    },
    {
      id: 2,
      label: t("packages.columns.bundleName"),
      field_name: "bundle_name",
      render: (row, rowIndex, handleFieldChange) => <>{row.name ?? "-"}</>,
      width: "200px",
    },
    {
      id: 3,
      label: t("packages.columns.description"),
      field_name: "description",
      render: (row, rowIndex, handleFieldChange) => (
        <>{row.description ?? "-"}</>
      ),
      width: "200px",
    },
    {
      id: 4,
      label: t("packages.columns.packages"),
      field_name: "total_packages",
      render: (row, rowIndex, handleFieldChange) => (
        <>{row?.packages?.length ?? "-"}</>
      ),
      width: "200px",
    },
    {
      id: 5,
      label: t("packages.columns.totalWeight"),
      field_name: "total_weight",
      render: (row, rowIndex, handleFieldChange) => (
        <>
          {row?.packages
            ?.map((packageItem) => parseFloat(packageItem.weight))
            ?.reduce((accumulator, weight) => accumulator + weight, 0)
            ?.toFixed(2) ??
            "-" ??
            "-"}
        </>
      ),
      width: "200px",
    },
    {
      id: 6,
      label: t("packages.columns.action"),
      field_name: "action",
      render: (row, rowIndex, handleFieldChange) => (
        <>
          <div className="table_action__wrap">
            <Tooltip title={t("packages.bundles.viewDetails")}>
              <div
                className="table_action__btn"
                onClick={() => setBundleDetailsModal(row)}
              >
                <MdRemoveRedEye size={10} />
                {t("packages.bundles.details")}
              </div>
            </Tooltip>
            <Tooltip title={t("packages.bundles.edit")}>
              <div
                className="table_action__btn"
                onClick={() => setUpdateBundleModal(row)}
              >
                <FaRegEdit size={10} />
                {t("common:actions.edit")}
              </div>
            </Tooltip>
            <Tooltip title={t("packages.bundles.delete")}>
              <div
                className="table_action__btn"
                onClick={() => setDeleteBundleModal(row.id)}
              >
                <MdOutlineCancel size={10} />
                {t("common:actions.delete")}
              </div>
            </Tooltip>
          </div>
        </>
      ),
      width: "200px",
    },
  ];

  const getAllBundles = async (page, perPage, filter) => {
    const response = await fetchAllBundles(page, perPage, filter);

    if (!response.error) {
      setBundles(response?.data?.result);
    } else {
      setSnack((snack) => {
        return {
          ...snack,
          open: true,
          text: t("common:messages.somethingWentWrong"),
          severity: "error",
        };
      });
    }
  };

  const deleteSingleBundle = async () => {
    setDeleteLoader(true);
    let id = deleteBundleModal;
    const deleted = await deleteBundle(deleteBundleModal);
    if (deleted.error) {
      setSnack((snack) => {
        return {
          ...snack,
          open: true,
          text: t("packages.bundles.deleteFailed"),
          severity: "error",
        };
      });
      setDeleteLoader(false);
    } else {
      setBundles((prevState) => {
        return {
          ...prevState,
          data: prevState?.data?.filter((bndl) => bndl?.id !== id),
        };
      });
      setSnack((snack) => {
        return {
          ...snack,
          open: true,
          text: t("packages.bundles.deleted"),
          severity: "success",
        };
      });
      setDeleteLoader(false);
      setDeleteBundleModal(false);
    }
  };

  return (
    <div>
      <Confirm
        open={deleteBundleModal}
        setOpen={setDeleteBundleModal}
        handleConfirm={deleteSingleBundle}
        text={t("packages.bundles.deleteConfirm")}
        loader={deleteLoader}
      />

      <AddBundle
        open={addBundleModal}
        setOpen={setAddBundleModal}
        setSnack={setSnack}
        setRefresh={setRefresh}
      />
      <BundleDetails
        open={!!bundleDetailsModal}
        bundleData={bundleDetailsModal}
        setOpen={setBundleDetailsModal}
      />
      <UpdateBundle
        open={updateBundleModal}
        bundlesData={updateBundleModal}
        setOpen={setUpdateBundleModal}
        setSnack={setSnack}
        setPackages={setBundles}
      />

      <div>
        <PackagesTable
          tableTitle={t("packages.bundles.tableTitle")}
          tableId="bundleListTableforShipper"
          message={t("packages.bundles.empty")}
          searchPlaceHolder={t("packages.bundles.search")}
          columns={columns}
          data={bundles}
          getAllData={getAllBundles}
          refresh={refresh}
          headerButtons={
            <div className="flex gap-1 flex-wrap">
              <BoltButton
                icon={MdAdd}
                label={t("packages.bundles.add")}
                onClick={() => setAddBundleModal(true)}
                variant="primary"
                size="sm"
              />
            </div>
          }
        />
      </div>
    </div>
  );
};

export default BundlesList;
